// DES1's admitted presentation tokens (src/design/presentation-tokens.mjs, policy
// poneva.presentation-tokens/1) as the choices the C2 controls offer. A slot offers only what
// both DES1's table admits for the channel and INS's manifest declares for that slot; the table
// order is kept (spacing steps ascend). Swatches use the interface's own var(--pc-*) colours.
import { formatCopy } from '@/i18n/ns';
import type { InspectCopyKey } from '@/i18n/ns/inspect';
import {
  COLOURS,
  SPACING,
  colourKeysFor,
  type PresentationChannel,
} from '../../../src/design/presentation-tokens.mjs';
import type { ColourToken, SpacingToken } from './presentation-controls';

type Copy = Record<InspectCopyKey, string>;

const COLOUR_NAMES: Record<string, InspectCopyKey> = {
  ink: 'colourInk',
  muted: 'colourMuted',
  paper: 'colourPaper',
  surface: 'colourSurface',
  soft: 'colourSoft',
  line: 'colourLine',
  'line-strong': 'colourLineStrong',
  success: 'colourSuccess',
  warning: 'colourWarning',
  danger: 'colourDanger',
  'success-bg': 'colourSuccessBg',
  'warning-bg': 'colourWarningBg',
  'danger-bg': 'colourDangerBg',
};

export function colourName(key: string, copy: Copy): string {
  const own = COLOUR_NAMES[key];
  if (own) return copy[own];
  const series = /^series-(\d+)$/u.exec(key);
  return series ? formatCopy(copy.colourSeries, { n: series[1] }) : key;
}

/** DES1's keys for the channel ∩ the slot's declared keys. Nothing declared, nothing offered. */
export function colourTokens(
  channel: PresentationChannel,
  declared: readonly string[] | null | undefined,
  copy: Copy,
): ColourToken[] {
  if (!declared?.length) return [];
  const slot = new Set(declared);
  return colourKeysFor(channel)
    .filter((key) => slot.has(key))
    .map((key) => ({ key, label: colourName(key, copy) }));
}

/** The swatch for an admitted key; null (not offered) for anything outside DES1's table. */
export function swatchFor(key: string): string | null {
  const colour = Object.hasOwn(COLOURS, key) ? COLOURS[key] : null;
  return colour ? `var(--pc-${colour.cssVar})` : null;
}

/** DES1's spacing steps the slot declares, smallest first; the stepper covers the rest. */
export function spacingTokens(
  declared: readonly string[] | null | undefined,
  copy: Copy,
): SpacingToken[] {
  if (!declared?.length) return [];
  const slot = new Set(declared);
  return Object.entries(SPACING)
    .filter(([key]) => slot.has(key))
    .sort(([, a], [, b]) => a.px - b.px)
    .map(([key, step]) => ({
      key,
      px: step.px,
      label: key.toUpperCase(),
    }));
}

/**
 * A presentation value in words, in the interface's language: what the root's `labels.value`
 * (ART's describePrepared) shows in before → after. Colour and spacing tokens use DES1's names
 * and px; image fit and ratio use the controls' own labels; text and anything else is shown as
 * given. A number format's display belongs to ART's formatter, not to this function.
 */
export function presentationValueLabel(
  property: string,
  value: unknown,
  copy: Copy,
  spacing: readonly SpacingToken[] = [],
): string {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  if (property === 'imageFit') return value === 'cover' ? copy.fitCover : copy.fitContain;
  if (property === 'imageRatio') return value === 'intrinsic' ? copy.ratioIntrinsic : String(value);
  if (property === 'colour' && typeof value === 'string')
    return value === 'none' ? copy.colourNone : colourName(value, copy);
  if (property === 'spacing') {
    const px =
      typeof value === 'number'
        ? value
        : (spacing.find((t) => t.key === value)?.px ?? Number(SPACING[value]?.px));
    return Number.isFinite(px) ? formatCopy(copy.pxValue, { n: px }) : String(value);
  }
  return String(value);
}
